import { CreateChildForm, CreateParentForm } from '@schooly/components/applications';
import { CreateCustomField } from '@schooly/components/applications';
import { useNotifications } from '@schooly/components/notifications';
import { useCustomFields } from '@schooly/hooks/use-custom-fields';
import { Loading, ModalLarge } from '@schooly/style';
import { getCustomFieldsWithEmptyValue } from '@schooly/utils/application-helpers';
import { FC, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { useApplication } from '../../../context/applications/useApplication';
import { useRouter } from '../../../context/router/useRouter';
import { useApplicationForm } from '../useApplicationForm';
import { ApplicationEditablePreviewContainer } from './ApplicationEditablePreviewContainer';

export const ApplicationCreatePreviewModal: FC = () => {
  const {
    addChild,
    addParents,
    addGeneralCustomFields,
    parents,
    children,
    generalCustomFields,
    resetForm,
  } = useApplicationForm('create');
  const { createApplication, fetching, isApplicationManager, setPendingParents, addParentModal } =
    useApplication();

  const { applicationAdultCustomFields, fetching: loadingAdultCustomFields } = useCustomFields();

  const { showNotification } = useNotifications();
  const { $t } = useIntl();

  const { goBack } = useRouter();
  const navigate = useNavigate();

  const handleConvert = useCallback(async () => {
    const applicationId = await createApplication({ parents, children, generalCustomFields });

    if (applicationId) {
      navigate(`/applications/${applicationId}/convert`, {
        replace: true,
      });
    }

    resetForm();
  }, [children, createApplication, navigate, parents, resetForm, generalCustomFields]);

  const handleSave = useCallback(async () => {
    const applicationId = await createApplication({ parents, children, generalCustomFields });

    navigate(`/applications`);

    if (applicationId) {
      showNotification({
        message: $t({ id: 'applications-SuccessfulCreate' }),
        type: 'success',
        actions: [
          {
            textId: 'applications-Action-View',
            handler: () =>
              navigate(`/applications/${applicationId}`, {
                replace: true,
              }),
            buttonColor: 'light',
          },
        ],
      });
    }

    resetForm();
  }, [
    $t,
    children,
    createApplication,
    navigate,
    parents,
    resetForm,
    showNotification,
    generalCustomFields,
  ]);

  const onChildSubmit = useCallback(
    (data: CreateChildForm) => {
      addChild(data);
    },
    [addChild],
  );

  const onParentSubmit = useCallback(
    (data: CreateParentForm) => {
      addParents([data]);
    },
    [addParents],
  );

  const onGeneralCustomFieldsSubmit = useCallback(
    (data: CreateCustomField[]) => {
      addGeneralCustomFields(data);
    },
    [addGeneralCustomFields],
  );

  const onAddExistingAdult = useCallback(
    (adult: CreateParentForm) => {
      const isParentAdded = parents.some((p) => p.profile_id === adult.profile_id);
      if (isParentAdded) return;

      const hasAdultCustomFields =
        adult.custom_fields.length > 0 || applicationAdultCustomFields.length > 0;

      if (hasAdultCustomFields) {
        const customFields = getCustomFieldsWithEmptyValue(applicationAdultCustomFields);
        setPendingParents([...parents, { ...adult, custom_fields: customFields }]);
        addParentModal.onOpen();
      } else {
        addParents([adult]);
      }

      navigate('/applications/new');
    },
    [
      parents,
      applicationAdultCustomFields,
      navigate,
      setPendingParents,
      addParentModal,
      addParents,
    ],
  );

  return (
    <ModalLarge open>
      {loadingAdultCustomFields ? (
        <Loading />
      ) : (
        <ApplicationEditablePreviewContainer
          onChildSubmit={onChildSubmit}
          onParentSubmit={onParentSubmit}
          onGeneralCustomFieldsSubmit={onGeneralCustomFieldsSubmit}
          onClose={goBack}
          onSave={handleSave}
          onConvert={handleConvert}
          pending={fetching}
          formData={{ children, parents, generalCustomFields }}
          onGoBack={goBack}
          canEdit={isApplicationManager}
          onAddExistingAdult={onAddExistingAdult}
          withSaveButton
        />
      )}
    </ModalLarge>
  );
};
